import Controller from '@ember/controller';
import { action, get } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import currency from 'currency.js';
import { type TabsComponentItem } from 'donor-elf-web/components/tabs';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { pledgeOneTimeFrequency } from 'donor-elf-web/lib/pledge-utils';
import { type YtdNextYearProjectionRouteModel } from 'donor-elf-web/routes/funds/fund/ytd-next-year-projection';
import AddSomethingModal, {
  type PledgeSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import ContactsCache from 'donor-elf-web/services/contacts-cache';

export default class YtdNextYearProjectionController extends Controller {
  @service declare addSomethingModal: AddSomethingModal;
  @service declare contactsCache: ContactsCache;

  model!: YtdNextYearProjectionRouteModel;
  queryParams = [{ activeTab: 'tab' }];
  savePledgeSubsciptionToken?: string;

  @tracked activeTab = 'Expecting Next Year';
  @tracked sortBy = 'expected_amount:desc';

  get goalAmount(): number {
    return this.model.record.goalAmount;
  }

  get startAt(): Date {
    return this.model.record.startAt;
  }

  get endAt(): Date {
    return this.model.record.endAt;
  }

  get tabs(): TabsComponentItem[] {
    return [
      {
        label: 'Expecting Next Year',
        active: this.activeTab === 'Expecting Next Year',
        onClick: () => {
          this.activeTab = 'Expecting Next Year';
          this.sortBy = 'expected_amount:desc';
        },
      },
      {
        label: 'Ending This Year',
        active: this.activeTab === 'Ending This Year',
        onClick: () => {
          this.activeTab = 'Ending This Year';
          this.sortBy = 'annual_amount:desc';
        },
      },
      {
        label: 'Ended Last Year',
        active: this.activeTab === 'Ended Last Year',
        onClick: () => {
          this.activeTab = 'Ended Last Year';
          this.sortBy = 'annual_amount:desc';
        },
      },
    ];
  }

  // excluding ended pledges from contacts that have pledges for next year or this year
  @cached
  get endedLastYearPledgeInfos() {
    let contactIdsForNextYearAndEndingThisYear: { [contactId: string]: true } =
      {};
    this.nextYearPledgeInfos.forEach((x) => {
      if (x.pledge.contactId) {
        contactIdsForNextYearAndEndingThisYear[x.pledge.contactId] = true;
      }
    });
    this.endingThisYearPledgeInfos.forEach((x) => {
      if (x.pledge.contactId) {
        contactIdsForNextYearAndEndingThisYear[x.pledge.contactId] = true;
      }
    });

    return sortByProperties(
      this.model.record.newPledgeData.ended_last_year
        .map((x) => {
          const pledge = this.model.pledgesById[x.pledge_id];

          return {
            contact: this.contactsCache.contactsById[pledge.contactId!],
            pledge: pledge,
            ...x,
          };
        })
        .filter((x) => {
          return (
            contactIdsForNextYearAndEndingThisYear[x.pledge.contactId!] !== true
          );
        }),
      [this.sortBy],
    );
  }

  get endedLastYearAmount() {
    return this.endedLastYearPledgeInfos.reduce(
      (sum: number, item) => currency(sum).add(item.annual_amount).value,
      0,
    );
  }

  get endedLastYearPercent() {
    return Math.round((this.endedLastYearAmount * 100) / this.goalAmount);
  }

  get endedLastYearDate() {
    return this.model.record.newPledgeData.last_year_end_date;
  }

  @cached
  get endingThisYearPledgeInfos() {
    return sortByProperties(
      this.model.record.newPledgeData.ending_this_year.map((x) => {
        const pledge = this.model.pledgesById[x.pledge_id];

        return {
          contact: this.contactsCache.contactsById[pledge.contactId!],
          pledge: pledge,
          ...x,
        };
      }),
      [this.sortBy],
    );
  }

  get endingThisYearAmount() {
    return this.endingThisYearPledgeInfos.reduce(
      (sum: number, item) => currency(sum).add(item.annual_amount).value,
      0,
    );
  }

  get endingThisYearPercent() {
    return Math.round((this.endingThisYearAmount * 100) / this.goalAmount);
  }

  get endingThisYearDate() {
    return this.model.record.newPledgeData.this_year_end_date;
  }

  @cached
  get nextYearPledgeInfos() {
    return sortByProperties(
      this.model.record.newPledgeData.next_year.map((x) => {
        const pledge = this.model.pledgesById[x.pledge_id];

        return {
          contact: this.contactsCache.contactsById[pledge.contactId!],
          pledge: pledge,
          ...x,
        };
      }),
      [this.sortBy],
    );
  }

  get nextYearRecurringPledgeInfos() {
    return this.nextYearPledgeInfos.filter(
      (x) => x.pledge.frequency !== pledgeOneTimeFrequency,
    );
  }

  get nextYearRecurringAmount() {
    return this.nextYearRecurringPledgeInfos.reduce(
      (sum: number, item) => currency(sum).add(item.expected_amount).value,
      0,
    );
  }

  get nextYearOneTimePledgeInfos() {
    return this.nextYearPledgeInfos.filter(
      (x) => x.pledge.frequency === pledgeOneTimeFrequency,
    );
  }

  get nextYearOneTimeAmount() {
    return this.nextYearOneTimePledgeInfos.reduce(
      (sum: number, item) => currency(sum).add(item.expected_amount).value,
      0,
    );
  }

  get nextYearExpectedAmount(): number {
    return currency(this.nextYearRecurringAmount).add(
      this.nextYearOneTimeAmount,
    ).value;
  }

  get nextYearExpectedPercent() {
    return Math.round((this.nextYearExpectedAmount * 100) / this.goalAmount);
  }

  @action
  templateDidInsert() {
    this.savePledgeSubsciptionToken =
      this.addSomethingModal.subscribeToPledgeSaved(this.handlePledgeSaved);
  }

  @action
  templateWillDestroy() {
    this.activeTab = 'Expecting Next Year';
    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
  }

  handleColumnSort = (sortBy: string) => {
    let [col, dir] = sortBy.split(':');
    dir ||= 'asc';

    const [currentCol, currentDir] = this.sortBy.split(':');

    if (col === currentCol) {
      if (currentDir === 'asc') {
        dir = 'desc';
      } else {
        dir = 'asc';
      }
    }

    this.sortBy = `${col}:${dir}`;
  };

  @action
  async handlePledgeSaved(_msg: string, _data: PledgeSavedArgs) {
    this.send('refreshYtdNextYearProjectionRoute');
  }

  @action
  async handleGoalAmountChanged() {
    let changeset = this.model.changeset;
    if (changeset.validate()) {
      let goalAmount = Number(get(changeset, 'goalAmount'));
      let record = this.model.record;
      if (goalAmount != record.goalAmount) {
        record.goalAmount = goalAmount;
        await record.save();
      }
    }
  }
}
